import { useGeckoAuth } from 'features/auth'
import { useRolodexEdit } from 'features/fde'
import { RolodexContext } from 'features/rolodex'
import { useSageService } from 'features/sage'
import { useContext } from 'react'
import {
  KVAnnotation,
  KVRooftopAnnotation,
  RolodexEntryPatchJson,
  RolodexFileRef,
  RooftopDamageId,
} from 'types'

export interface RooftopAnnotationAnalysisResult {
  annotationId: string
  success: boolean
  error?: string
  analysisData?: {
    annotationName: string
    failureModes: Array<{
      failureMode: string
      evidence: string
    }>
    criticality: number
  }
}

export interface AnalyzeAnnotationsOptions {
  model?: 'gemini' | 'claude37'
  additionalInstructions?: string
}

function mapFailureModeToRooftopDamageId(
  failureMode: string
): RooftopDamageId | null {
  const lowerFailureMode = failureMode.toLowerCase().trim()

  const exactMappings: Record<string, RooftopDamageId> = {
    'membrane seam delamination': RooftopDamageId.SeamFailuresSplitSeams,
    'insulation saturation / r-value loss': RooftopDamageId.WetInsulation,
    'ponding / negative drainage slope':
      RooftopDamageId.MoistureIntrusionWaterPonding,
    'flashing failure (curbs, edges, transitions)':
      RooftopDamageId.LooseOrDetachedFlashing,
    'perimeter uplift / wind damage': RooftopDamageId.WindUpliftWindScour,
    'capillary action / seam capillary wicking':
      RooftopDamageId.CapillaryMoistureIntrusion,
    'mechanical base intrusion (hvac/rtus)':
      RooftopDamageId.ImproperHvacUnitMounting,
    'structural deck deflection or collapse':
      RooftopDamageId.SaggingDeckSubstrate,
    'termination bar failure': RooftopDamageId.OpenTerminationsEdges,
    'wet underlayment / trapped vapor': RooftopDamageId.BlistersAndBubbles,
    'uv & thermal fatigue cracking': RooftopDamageId.UvDamageBrittleness,
    'debris / vegetation growth': RooftopDamageId.TreeLimbOrDebrisDamage,
    'electrical conduit leaks (edmp systems)':
      RooftopDamageId.MoistureIntrusionWaterPonding,
    'ballast movement / displacement': RooftopDamageId.WindUpliftWindScour,
    'parapet wall water retention': RooftopDamageId.ParapetWallDeterioration,
    'overflow drainage failure/drain head clamp ring failure':
      RooftopDamageId.CloggedRoofDrainsScuppersGutters,
    'membrane shrinkage / pullback': RooftopDamageId.MembraneShrinkage,
    'thermal bridging (fasteners, decking)':
      RooftopDamageId.ThermalBridgingInsulationExposure,
  }

  if (exactMappings[lowerFailureMode]) {
    return exactMappings[lowerFailureMode]
  }

  const keywordMappings: Record<string, RooftopDamageId> = {
    'membrane seam': RooftopDamageId.SeamFailuresSplitSeams,
    'seam delamination': RooftopDamageId.SeamFailuresSplitSeams,
    'insulation saturation': RooftopDamageId.WetInsulation,
    'r-value loss': RooftopDamageId.WetInsulation,
    'wet insulation': RooftopDamageId.WetInsulation,
    ponding: RooftopDamageId.MoistureIntrusionWaterPonding,
    'standing water': RooftopDamageId.MoistureIntrusionWaterPonding,
    'drainage slope': RooftopDamageId.ImproperDrainageSlope,
    'negative drainage': RooftopDamageId.ImproperDrainageSlope,
    'flashing failure': RooftopDamageId.LooseOrDetachedFlashing,
    'loose flashing': RooftopDamageId.LooseOrDetachedFlashing,
    'detached flashing': RooftopDamageId.LooseOrDetachedFlashing,
    'wind damage': RooftopDamageId.WindUpliftWindScour,
    'wind uplift': RooftopDamageId.WindUpliftWindScour,
    'perimeter uplift': RooftopDamageId.WindUpliftWindScour,
    'capillary action': RooftopDamageId.CapillaryMoistureIntrusion,
    'capillary wicking': RooftopDamageId.CapillaryMoistureIntrusion,
    hvac: RooftopDamageId.ImproperHvacUnitMounting,
    'mechanical base': RooftopDamageId.ImproperHvacUnitMounting,
    'deck deflection': RooftopDamageId.SaggingDeckSubstrate,
    'deck collapse': RooftopDamageId.SaggingDeckSubstrate,
    'sagging deck': RooftopDamageId.SaggingDeckSubstrate,
    'termination bar': RooftopDamageId.OpenTerminationsEdges,
    'open termination': RooftopDamageId.OpenTerminationsEdges,
    'trapped vapor': RooftopDamageId.BlistersAndBubbles,
    'wet underlayment': RooftopDamageId.BlistersAndBubbles,
    vapor: RooftopDamageId.CondensationDamage,
    'uv damage': RooftopDamageId.UvDamageBrittleness,
    'thermal fatigue': RooftopDamageId.UvDamageBrittleness,
    'uv cracking': RooftopDamageId.UvDamageBrittleness,
    debris: RooftopDamageId.TreeLimbOrDebrisDamage,
    'vegetation growth': RooftopDamageId.MossOrAlgaeGrowth,
    moss: RooftopDamageId.MossOrAlgaeGrowth,
    algae: RooftopDamageId.MossOrAlgaeGrowth,
    'electrical conduit': RooftopDamageId.MoistureIntrusionWaterPonding,
    'ballast movement': RooftopDamageId.WindUpliftWindScour,
    'ballast displacement': RooftopDamageId.WindUpliftWindScour,
    'parapet wall': RooftopDamageId.ParapetWallDeterioration,
    'water retention': RooftopDamageId.MoistureIntrusionWaterPonding,
    'overflow drainage': RooftopDamageId.CloggedRoofDrainsScuppersGutters,
    'drain failure': RooftopDamageId.CloggedRoofDrainsScuppersGutters,
    'clogged drain': RooftopDamageId.CloggedRoofDrainsScuppersGutters,
    'membrane shrinkage': RooftopDamageId.MembraneShrinkage,
    pullback: RooftopDamageId.MembraneShrinkage,
    'thermal bridging': RooftopDamageId.ThermalBridgingInsulationExposure,
    blister: RooftopDamageId.BlistersAndBubbles,
    bubble: RooftopDamageId.BlistersAndBubbles,
    delamination: RooftopDamageId.Delamination,
    crack: RooftopDamageId.Cracks,
    puncture: RooftopDamageId.Punctures,
    tear: RooftopDamageId.CutsAndTears,
  }

  for (const [keyword, damageId] of Object.entries(keywordMappings)) {
    if (lowerFailureMode.includes(keyword)) {
      return damageId
    }
  }

  const normalized = failureMode
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[()]/g, '')
    .replace(/\//g, '-')
    .replace(/,/g, '')

  const allDamageIds = Object.values(RooftopDamageId) as string[]

  const exactMatch = allDamageIds.find((id) => id === normalized)
  if (exactMatch) {
    return exactMatch as RooftopDamageId
  }

  return null
}

export function useRooftopAnnotationAnalysis() {
  const { annotateRooftopImages } = useSageService()
  const { rolodexPatch } = useRolodexEdit()
  const { authorizedAxios } = useGeckoAuth()
  const { apiURL } = useContext(RolodexContext)

  const analyzeAnnotation = async (
    annotation: KVAnnotation,
    kestrelModelId: string,
    options: AnalyzeAnnotationsOptions = {}
  ): Promise<RooftopAnnotationAnalysisResult> => {
    const { model = 'gemini', additionalInstructions } = options

    try {
      if (!annotation.rooftopAnnotation) {
        annotation.rooftopAnnotation = new KVRooftopAnnotation()
      }

      const screenshotBasePath = `Screenshots/${annotation.id}/**`
      let fileRefs: RolodexFileRef[] = []
      try {
        const listFilesResponse = await authorizedAxios.post(
          `${apiURL}/api/v2/files/ls`,
          JSON.stringify({
            entry_id: kestrelModelId,
            path: screenshotBasePath,
          }),
          {
            headers: { 'Content-Type': 'application/json' },
          }
        )
        fileRefs = listFilesResponse.data || []
      } catch {
        return {
          annotationId: annotation.id,
          success: false,
          error: 'Failed to list screenshot files for annotation',
        }
      }

      const imageFiles = fileRefs.filter((file) => {
        const lowerPath = file.path.toLowerCase()
        return (
          lowerPath.endsWith('.png') ||
          lowerPath.endsWith('.jpg') ||
          lowerPath.endsWith('.jpeg')
        )
      })

      if (imageFiles.length === 0) {
        return {
          annotationId: annotation.id,
          success: false,
          error: 'No image screenshots found for this annotation',
        }
      }

      const rolodexFiles = imageFiles.map((file) => {
        const lowerPath = file.path.toLowerCase()
        const mimeType = lowerPath.endsWith('.png') ? 'img/png' : 'img/jpeg'
        return {
          id: kestrelModelId,
          path: file.path,
          mime_type: mimeType,
        }
      })
      const response = await annotateRooftopImages({
        model,
        rolodex_files: rolodexFiles,
        additional_instructions: additionalInstructions,
      })

      let parsedResult
      try {
        parsedResult = JSON.parse(response.result)
      } catch (parseError) {
        console.error(parseError, response.result)
        return {
          annotationId: annotation.id,
          success: false,
          error: 'Failed to parse analysis response',
        }
      }

      if (
        !parsedResult.annotation_name ||
        typeof parsedResult.annotation_name !== 'string' ||
        !Array.isArray(parsedResult.failure_modes) ||
        typeof parsedResult.criticality !== 'number' ||
        parsedResult.criticality < 1 ||
        parsedResult.criticality > 5
      ) {
        console.error(
          `[analyzeAnnotation] Invalid analysis response structure for ${annotation.id}:`,
          parsedResult
        )
        return {
          annotationId: annotation.id,
          success: false,
          error: 'Invalid analysis response structure',
        }
      }

      const failureModes = parsedResult.failure_modes
        .filter(
          (fm: unknown) =>
            typeof fm === 'object' &&
            fm !== null &&
            'failure_mode' in fm &&
            'evidence' in fm &&
            typeof (fm as { failure_mode: unknown }).failure_mode ===
              'string' &&
            typeof (fm as { evidence: unknown }).evidence === 'string'
        )
        .map((fm: { failure_mode: string; evidence: string }) => ({
          failureMode: fm.failure_mode,
          evidence: fm.evidence,
        }))

      const analysisData = {
        annotationName: parsedResult.annotation_name,
        failureModes,
        criticality: parsedResult.criticality,
      }

      annotation.severity = analysisData.criticality.toString()
      annotation.rooftopAnnotation.failureModes = analysisData.failureModes

      const mappedDamageIds = failureModes
        .map((fm: { failureMode: string; evidence: string }) =>
          mapFailureModeToRooftopDamageId(fm.failureMode)
        )
        .filter(
          (id: RooftopDamageId | null): id is RooftopDamageId => id !== null
        )

      annotation.rooftopAnnotation.selected = Array.from(
        new Set(mappedDamageIds)
      )

      const evidenceNotes = analysisData.failureModes
        .map(
          (fm: { failureMode: string; evidence: string }) =>
            `${fm.failureMode}: ${fm.evidence}`
        )
        .join('\n\n')
      annotation.notes = evidenceNotes

      return {
        annotationId: annotation.id,
        success: true,
        analysisData,
      }
    } catch (error) {
      console.error(
        `[analyzeAnnotation] Error analyzing annotation ${annotation.id}:`,
        error
      )
      return {
        annotationId: annotation.id,
        success: false,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      }
    }
  }

  const analyzeAnnotations = async (
    annotations: KVAnnotation[],
    kestrelModelId: string,
    options: AnalyzeAnnotationsOptions = {}
  ): Promise<RooftopAnnotationAnalysisResult[]> => {
    const results: RooftopAnnotationAnalysisResult[] = []

    for (const annotation of annotations) {
      const result = await analyzeAnnotation(
        annotation,
        kestrelModelId,
        options
      )
      results.push(result)
    }

    return results
  }

  const patchAnnotationsToRolodex = async (
    annotations: KVAnnotation[],
    kestrelModelId: string
  ): Promise<void> => {
    const patchData: RolodexEntryPatchJson = {
      id: kestrelModelId,
      data: {
        annotations: annotations.map((a) => KVAnnotation.toJSON(a)),
      },
    }

    await rolodexPatch([patchData])
  }

  return {
    analyzeAnnotation,
    analyzeAnnotations,
    patchAnnotationsToRolodex,
  }
}
